import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTiktok,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

const socials = [
  {
    icon: <FaFacebookF />,
    label: "Facebook",
    color: "#1877F2",
    href: "https://www.facebook.com/share/18vdJmDF1i/",
  },
  {
    icon: <FaInstagram />,
    label: "Instagram",
    color: "#E1306C",
    href: "https://www.instagram.com/kitabonkidolat?igsh=cTV5amZrcmV4ZjNy",
  },
  {
    icon: <FaTiktok />,
    label: "TikTok",
    color: "#000000",
    href: "https://www.tiktok.com/@kitabon.ki.dolat?_r=1&_t=ZS-96fIBOm3b3V",
  },
  {
    icon: <FaWhatsapp />,
    label: "WhatsApp",
    color: "#25D366",
    href: "https://wa.me/923410889909",
  },
  {
    icon: <FaYoutube />,
    label: "YouTube",
    color: "#FF0000",
    href: "https://www.youtube.com/channel/UCHwLJpNUrRv8iFN_YAwEiHg",
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, openAuthModal } = useAuth();

  const handleScroll = (event, targetId) => {
    event.preventDefault();

    if (targetId === "subscriptions" && !isLoggedIn) {
      openAuthModal();
      return;
    }

    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: targetId } });
    } else {
      const target = document.getElementById(targetId);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderFooterLink = ([target, label, isScroll]) => (
    <li key={target}>
      {isScroll ? (
        <a
          href={target}
          onClick={(event) => handleScroll(event, target.slice(1))}
          className="footer-link"
        >
          <span className="footer-link-arrow">-&gt;</span>
          {label}
        </a>
      ) : (
        <Link to={target} className="footer-link">
          <span className="footer-link-arrow">-&gt;</span>
          {label}
        </Link>
      )}
    </li>
  );

  return (
    <footer className="footer-root">
      <div className="footer-bg" aria-hidden="true">
        <div className="footer-orb footer-orb-1" />
        <div className="footer-orb footer-orb-2" />
        <div className="footer-orb footer-orb-3" />
        <div className="footer-top-line" />
        <div className="footer-grid-lines" />
      </div>

      <div className="footer-main">
        <div className="footer-brand">
          <div
            className="footer-logo-wrap"
            onClick={() => navigate("/")}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => event.key === "Enter" && navigate("/")}
          >
            <img
              src="/logo-small.webp"
              alt="Kitabon Ki Dolat"
              className="footer-logo-img"
              loading="lazy"
              decoding="async"
              width="112"
              height="92"
            />
          </div>
          <p className="footer-tagline">
            Pakistan's premier destination for quality literature - bridging
            readers with knowledge, culture, and stories that last a lifetime.
          </p>

          <div className="footer-contact-info">
            <div className="footer-contact-item">
              <FaEnvelope className="footer-contact-icon" />
              <span>readersshub@gmail.com</span>
            </div>
            <div className="footer-contact-item">
              <FaPhoneAlt className="footer-contact-icon" />
              <span dir="ltr">+92 341 0889909</span>
            </div>
            <div className="footer-contact-item">
              <FaMapMarkerAlt className="footer-contact-icon" />
              <span>House no #426, Block 5, Sector D1, Green town, Lahore, Punjab</span>
            </div>
          </div>

          <div className="footer-socials">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                aria-label={social.label}
                className="footer-social-btn"
                style={{ "--social-color": social.color }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        <div className="footer-col">
          <h3 className="footer-col-heading">Explore</h3>
          <ul className="footer-links">
            {[
              ["/books", "All Books", false],
              ["/audiobooks", "Audiobooks", false],
              ["/events", "Celebrity Events", false],
              ["#subscriptions", "Subscriptions", true],
            ].map(renderFooterLink)}
          </ul>
        </div>

        <div className="footer-col">
          <h3 className="footer-col-heading">Support</h3>
          <ul className="footer-links">
            {[
              ["/faqs", "FAQs", false],
              ["/contact", "Contact Us", false],
              ["/shipping", "Shipping Info", false],
            ].map(renderFooterLink)}
          </ul>
        </div>

        <div className="footer-col">
          <h3 className="footer-col-heading">Company</h3>
          <ul className="footer-links">
            {[
              ["/about-author", "About Dolat", false],
              ["/privacy", "Privacy Policy", false],
              ["/refund", "Refund Policy", false],
              ["/terms", "Terms of Service", false],
            ].map(renderFooterLink)}
          </ul>

          <div className="footer-newsletter">
            <p className="footer-newsletter-label">Get updates</p>
            <div className="footer-newsletter-row">
              <input
                type="email"
                placeholder="your@email.com"
                className="footer-newsletter-input"
              />
              <button type="button" className="footer-newsletter-btn">
                -&gt;
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-inner">
          <p className="footer-copyright">
            Copyright 2026 Kitabon Ki Dolat. All rights reserved.
          </p>
          <div className="footer-bottom-badges">
            <span className="footer-badge">Made in Pakistan</span>
            <span className="footer-badge">Free Delivery on 2k+</span>
            <span className="footer-badge">Secure Payments</span>
          </div>
          <p className="footer-credit">
            Designed by Dolat Khan Kakar
          </p>
        </div>
      </div>
    </footer>
  );
}
